import { useState, useMemo, useEffect } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useAuth } from "@/contexts/AuthContext";
import { validateContraVoucher } from "@/lib/accounting/engine";
import { VoucherLine } from "@/lib/accounting/types";
import { Button } from "@/components/ui/button";
import { FormModal } from "@/components/form/FormModal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ArrowRightLeft, Landmark, Wallet, CheckCircle2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ContraVoucherFormProps {
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  defaultTransferType?: "deposit" | "withdrawal" | "transfer";
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ContraVoucherForm({
  trigger,
  onSuccess,
  defaultTransferType = "deposit",
  isOpen,
  onOpenChange,
}: ContraVoucherFormProps) {
  const { ledgers, createVoucher } = useAccounting();
  const { role } = useAuth();
  const { toast } = useToast();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = isOpen !== undefined ? isOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const [transferType, setTransferType] = useState<"deposit" | "withdrawal" | "transfer">(defaultTransferType);
  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [sourceLedgerId, setSourceLedgerId] = useState("");
  const [destLedgerId, setDestLedgerId] = useState("");
  const [amount, setAmount] = useState<string>("");
  const [instrumentNo, setInstrumentNo] = useState("");
  const [instrumentDate, setInstrumentDate] = useState("");
  const [narration, setNarration] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Filter Cash and Bank Ledgers
  const cashLedgers = useMemo(() => ledgers.filter((l) => l.group === "Cash-in-Hand"), [ledgers]);
  const bankLedgers = useMemo(() => ledgers.filter((l) => l.group === "Bank Accounts"), [ledgers]);
  const allCashBank = useMemo(() => [...cashLedgers, ...bankLedgers], [cashLedgers, bankLedgers]);

  // Adjust source/dest defaults based on transferType
  const handleTransferTypeChange = (type: "deposit" | "withdrawal" | "transfer") => {
    setTransferType(type);
    if (type === "deposit") {
      setSourceLedgerId(cashLedgers[0]?.id || "");
      setDestLedgerId(bankLedgers[0]?.id || "");
    } else if (type === "withdrawal") {
      setSourceLedgerId(bankLedgers[0]?.id || "");
      setDestLedgerId(cashLedgers[0]?.id || "");
    } else {
      setSourceLedgerId(bankLedgers[0]?.id || "");
      setDestLedgerId(bankLedgers[1]?.id || "");
    }
  };

  useEffect(() => {
    if (open) {
      handleTransferTypeChange(defaultTransferType);
    }
  }, [open, defaultTransferType, cashLedgers.length, bankLedgers.length]);

  const sourceLedger = useMemo(() => allCashBank.find((l) => l.id === sourceLedgerId), [allCashBank, sourceLedgerId]);
  const destLedger = useMemo(() => allCashBank.find((l) => l.id === destLedgerId), [allCashBank, destLedgerId]);

  const numAmount = parseFloat(amount) || 0;

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!sourceLedger || !destLedger) {
      toast({ title: "Account Required", description: "Please select both Source and Destination accounts.", variant: "destructive" });
      return;
    }
    if (sourceLedger.id === destLedger.id) {
      toast({ title: "Invalid Accounts", description: "Source and Destination accounts must be different.", variant: "destructive" });
      return;
    }
    if (numAmount <= 0) {
      toast({ title: "Invalid Amount", description: "Transfer amount must be greater than zero.", variant: "destructive" });
      return;
    }

    const validation = validateContraVoucher(destLedger.group, sourceLedger.group, numAmount);
    if (!validation.valid) {
      toast({ title: "Contra Rule Violation", description: validation.error, variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      // In double-entry:
      // Destination Account (receiving money) is DEBITED
      // Source Account (giving money) is CREDITED
      const lines: VoucherLine[] = [
        {
          id: crypto.randomUUID(),
          ledgerId: destLedger.id,
          ledgerName: destLedger.name,
          debit: numAmount,
          credit: 0,
          narration: `Contra Transfer received from ${sourceLedger.name}`,
        },
        {
          id: crypto.randomUUID(),
          ledgerId: sourceLedger.id,
          ledgerName: sourceLedger.name,
          debit: 0,
          credit: numAmount,
          narration: `Contra Transfer sent to ${destLedger.name}`,
        },
      ];

      const defaultNarration =
        transferType === "deposit"
          ? `Cash deposited into ${destLedger.name}${instrumentNo ? ` | Ref: ${instrumentNo}` : ""}`
          : transferType === "withdrawal"
          ? `Cash withdrawn from ${sourceLedger.name}${instrumentNo ? ` | Cheque/Ref: ${instrumentNo}` : ""}`
          : `Funds transferred from ${sourceLedger.name} to ${destLedger.name}${instrumentNo ? ` | UTR: ${instrumentNo}` : ""}`;

      const finalNarration = narration.trim() || defaultNarration;

      const result = await createVoucher(
        "Contra",
        lines,
        finalNarration,
        undefined,
        undefined,
        0, 0, 0, 0,
        undefined,
        {
          date,
          paymentMode: transferType === "transfer" ? "Bank Transfer" : "Cash",
          instrumentNo: instrumentNo.trim() || undefined,
          instrumentDate: instrumentDate || undefined,
          bankName: destLedger.group === "Bank Accounts" ? destLedger.name : sourceLedger.name,
          bankLedgerId: destLedger.group === "Bank Accounts" ? destLedger.id : sourceLedger.id,
        }
      );

      if (result.success && result.voucher) {
        toast({
          title: "Contra Voucher Created ✓",
          description: `${result.voucher.voucherNumber}: ₹${numAmount.toLocaleString("en-IN")} transferred successfully.`,
        });
        setOpen(false);
        setAmount("");
        setInstrumentNo("");
        setNarration("");
        onSuccess?.();
      } else {
        toast({ title: "Transfer Failed", description: result.error || "Unable to save contra entry.", variant: "destructive" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={open}
      onOpenChange={setOpen}
      size="transaction"
      trigger={
        trigger ? (
          trigger
        ) : isOpen === undefined ? (
          <Button variant="outline" className="gap-2">
            <ArrowRightLeft className="h-4 w-4" />
            <span>New Contra (F4)</span>
          </Button>
        ) : undefined
      }
      icon={<ArrowRightLeft className="h-5 w-5 text-indigo-500" />}
      title="Contra Voucher Entry (F4)"
      description="Record cash deposits, cash withdrawals, and bank-to-bank transfers without affecting business revenue or expenses."
      footerLeft={
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(false)}
          disabled={submitting}
          className="text-xs h-9"
        >
          Cancel <span className="text-[10px] text-muted-foreground ml-1">Esc</span>
        </Button>
      }
      footerRight={
        <Button
          type="submit"
          form="contra-voucher-form"
          disabled={submitting}
          className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs h-9 font-semibold gap-1.5 shadow-sm"
        >
          {submitting ? "Posting Contra..." : "Record Contra Voucher"}
        </Button>
      }
    >
      <form id="contra-voucher-form" onSubmit={handleSubmit} className="space-y-4">
          {/* Transfer Type Selector */}
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => handleTransferTypeChange("deposit")}
              className={`p-2.5 rounded-lg border text-xs font-semibold flex flex-col items-center gap-1 transition-all ${
                transferType === "deposit"
                  ? "bg-primary/10 border-primary text-primary"
                  : "bg-card border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              <div className="flex items-center gap-1">
                <Wallet className="h-3.5 w-3.5" />
                <span>→</span>
                <Landmark className="h-3.5 w-3.5" />
              </div>
              <span>Cash Deposit</span>
            </button>

            <button
              type="button"
              onClick={() => handleTransferTypeChange("withdrawal")}
              className={`p-2.5 rounded-lg border text-xs font-semibold flex flex-col items-center gap-1 transition-all ${
                transferType === "withdrawal"
                  ? "bg-primary/10 border-primary text-primary"
                  : "bg-card border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              <div className="flex items-center gap-1">
                <Landmark className="h-3.5 w-3.5" />
                <span>→</span>
                <Wallet className="h-3.5 w-3.5" />
              </div>
              <span>Cash Withdrawal</span>
            </button>

            <button
              type="button"
              onClick={() => handleTransferTypeChange("transfer")}
              className={`p-2.5 rounded-lg border text-xs font-semibold flex flex-col items-center gap-1 transition-all ${
                transferType === "transfer"
                  ? "bg-primary/10 border-primary text-primary"
                  : "bg-card border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              <div className="flex items-center gap-1">
                <Landmark className="h-3.5 w-3.5" />
                <span>⇄</span>
                <Landmark className="h-3.5 w-3.5" />
              </div>
              <span>Bank Transfer</span>
            </button>
          </div>

          {/* Date & Amount */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Transaction Date *
              </label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-9 text-xs font-mono"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Amount (₹) *
              </label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="h-9 text-xs font-mono font-semibold"
                required
                autoFocus
              />
            </div>
          </div>

          {/* Accounts: Source (Credit) & Destination (Debit) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                From Account (Credit) *
              </label>
              <Select value={sourceLedgerId} onValueChange={setSourceLedgerId}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select paying account..." />
                </SelectTrigger>
                <SelectContent>
                  {allCashBank.map((l) => (
                    <SelectItem key={l.id} value={l.id} className="text-xs">
                      {l.name} ({l.group}) · Bal: ₹{l.currentBalance.toLocaleString("en-IN")} {l.currentBalanceType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                To Account (Debit) *
              </label>
              <Select value={destLedgerId} onValueChange={setDestLedgerId}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select receiving account..." />
                </SelectTrigger>
                <SelectContent>
                  {allCashBank.map((l) => (
                    <SelectItem key={l.id} value={l.id} className="text-xs">
                      {l.name} ({l.group}) · Bal: ₹{l.currentBalance.toLocaleString("en-IN")} {l.currentBalanceType}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Instrument / Cheque / UTR */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Cheque / UTR / Reference No
              </label>
              <Input
                type="text"
                placeholder="e.g. CHQ-49201 or UTR-938210"
                value={instrumentNo}
                onChange={(e) => setInstrumentNo(e.target.value)}
                className="h-9 text-xs font-mono"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
                Instrument Date
              </label>
              <Input
                type="date"
                value={instrumentDate}
                onChange={(e) => setInstrumentDate(e.target.value)}
                className="h-9 text-xs font-mono"
              />
            </div>
          </div>

          {/* Narration */}
          <div>
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1">
              Narration / Remarks
            </label>
            <Input
              type="text"
              placeholder="e.g. Cash deposited into current account"
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          {/* Double-Entry Preview Box */}
          {sourceLedger && destLedger && numAmount > 0 && (
            <div className="p-2.5 rounded-lg bg-muted/40 border border-border text-xs space-y-1">
              <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[10px]">
                Double-Entry Preview:
              </span>
              <div className="flex justify-between items-center text-foreground font-mono">
                <span>Dr. {destLedger.name} (Deposit)</span>
                <span>₹{numAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-muted-foreground font-mono pl-4">
                <span>Cr. {sourceLedger.name} (Source)</span>
                <span>₹{numAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}

      </form>
    </FormModal>
  );
}
